import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, Cell, Legend, FunnelChart, Funnel, LabelList
} from 'recharts';
import { 
  Package, CheckCircle2, Truck, ShoppingCart, AlertTriangle, 
  TrendingUp, Building2, XCircle
} from 'lucide-react';
import THRDistrictDetailModal from '../../components/THRDistrictDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HCMEnhancedDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // State for all data
  const [stateOverview, setStateOverview] = useState(null);
  const [districtProgress, setDistrictProgress] = useState(null);
  const [issuanceAnalysis, setIssuanceAnalysis] = useState(null);
  const [allocationVariance, setAllocationVariance] = useState(null);
  const [redFlags, setRedFlags] = useState(null);
  const [scatterData, setScatterData] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      const [overview, progress, issuance, variance, flags, scatter] = await Promise.all([
        axios.get(`${API_URL}/api/dashboards/hcm-enhanced/state-overview`),
        axios.get(`${API_URL}/api/dashboards/hcm-enhanced/district-progress`),
        axios.get(`${API_URL}/api/dashboards/hcm-enhanced/issuance-analysis`),
        axios.get(`${API_URL}/api/dashboards/hcm-enhanced/allocation-variance`),
        axios.get(`${API_URL}/api/dashboards/hcm-enhanced/red-flag-districts`),
        axios.get(`${API_URL}/api/dashboards/hcm-enhanced/supply-chain-scatter`)
      ]);

      setStateOverview(overview.data);
      setDistrictProgress(progress.data);
      setIssuanceAnalysis(issuance.data);
      setAllocationVariance(variance.data);
      setRedFlags(flags.data);
      setScatterData(scatter.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading HCM enhanced data:', error);
      setLoading(false);
    }
  };

  // Reusable KPI Card Component
  const KPICard = ({ title, value, unit, icon: Icon, color, subtitle }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-full`} style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
        </div>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <p className="text-3xl font-bold" style={{ color }}>{value}{unit}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading HCM Dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PAGE 1: State Overview
  const OverviewTab = () => {
    if (!stateOverview) return <div>No data available</div>;

    const funnelData = [
      { name: 'Allocated', value: stateOverview.allocated_qty, fill: '#3b82f6' },
      { name: 'Permitted', value: stateOverview.permit_qty, fill: '#22c55e' },
      { name: 'Issued', value: stateOverview.issued_qty, fill: '#f59e0b' },
      { name: 'Sale', value: stateOverview.sale_qty, fill: '#ef4444' }
    ];

    return (
      <div className="space-y-6">
        {/* Top KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Total AWCs"
            value={stateOverview.total_awcs.toLocaleString()}
            unit=""
            icon={Building2}
            color="#3b82f6"
            subtitle={`${stateOverview.total_districts} districts`}
          />
          <KPICard
            title="Rice Allocated (4-month)"
            value={(stateOverview.allocated_qty / 1000).toFixed(1)}
            unit="T"
            icon={Package}
            color="#22c55e"
            subtitle={`${stateOverview.avg_allocated_per_awc} kg/AWC`}
          />
          <KPICard
            title="Permit Completion"
            value={stateOverview.permit_pct}
            unit="%"
            icon={CheckCircle2}
            color="#10b981"
            subtitle={`${(stateOverview.permit_qty / 1000).toFixed(1)}T permitted`}
          />
          <KPICard
            title="Issue Progress"
            value={stateOverview.issue_pct}
            unit="%"
            icon={Truck}
            color="#f59e0b"
            subtitle={`${stateOverview.issued_qty.toLocaleString()} kg issued`}
          />
        </div>

        {/* Second Row KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <KPICard
            title="Districts Started Issuance"
            value={stateOverview.districts_started}
            unit={`/${stateOverview.total_districts}`}
            icon={TrendingUp}
            color="#8b5cf6"
            subtitle="Active distribution"
          />
          <KPICard
            title="Zero Issuance Districts"
            value={stateOverview.districts_zero_issuance}
            unit=""
            icon={XCircle}
            color="#ef4444"
            subtitle="Require immediate action"
          />
          <KPICard
            title="Sale/Utilization"
            value={stateOverview.sale_pct}
            unit="%"
            icon={ShoppingCart}
            color="#06b6d4"
            subtitle="Not yet recorded"
          />
        </div>

        {/* Funnel Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Supply Chain Funnel: Oct 2025 - Jan 2026</CardTitle>
            <CardDescription>Allocation → Permit → Issue → Sale progression</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={300}>
                <FunnelChart>
                  <Tooltip />
                  <Funnel dataKey="value" data={funnelData}>
                    <LabelList position="right" fill="#000" stroke="none" dataKey="name" />
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold text-blue-900">Stage 1: Allocated</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {(stateOverview.allocated_qty / 1000).toFixed(1)}T
                  </p>
                  <p className="text-xs text-blue-700 mt-1">Full 4-month cycle planned</p>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm font-semibold text-green-900">Stage 2: Permitted</p>
                  <p className="text-2xl font-bold text-green-600">
                    {(stateOverview.permit_qty / 1000).toFixed(1)}T ({stateOverview.permit_pct}%)
                  </p>
                  <p className="text-xs text-green-700 mt-1">Approval complete</p>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm font-semibold text-orange-900">Stage 3: Issued</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {(stateOverview.issued_qty / 1000).toFixed(1)}T ({stateOverview.issue_pct}%)
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    Just started - {stateOverview.districts_started} districts
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* District Issue Progress */}
        {districtProgress && (
          <Card>
            <CardHeader>
              <CardTitle>District Issue Progress</CardTitle>
              <CardDescription>Issue % vs Allocation (sorted lowest first)</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart 
                  data={districtProgress.districts} 
                  layout="vertical"
                  margin={{ left: 120 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 10]} />
                  <YAxis dataKey="district_id" type="category" />
                  <Tooltip />
                  <Bar dataKey="issue_pct" radius={[0, 8, 8, 0]}>
                    {districtProgress.districts.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={
                          entry.issue_pct >= 5 ? '#22c55e' : 
                          entry.issue_pct >= 1 ? '#f59e0b' : '#ef4444'
                        } 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Allocation per AWC */}
        {allocationVariance && (
          <Card>
            <CardHeader>
              <CardTitle>Allocation per AWC Distribution</CardTitle>
              <CardDescription>Wide variance: 10x difference between highest and lowest</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-green-900 mb-3">Top 5 - High Allocation</h4>
                  <div className="space-y-2">
                    {allocationVariance.top_5.map((d, idx) => (
                      <div key={idx} className="flex justify-between p-2 bg-green-50 rounded">
                        <span className="text-sm font-medium">{d.district_id}</span>
                        <span className="text-sm font-bold text-green-600">
                          {d.allocated_per_awc} kg/AWC
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-red-900 mb-3">Bottom 5 - Low Allocation</h4>
                  <div className="space-y-2">
                    {allocationVariance.bottom_5.map((d, idx) => (
                      <div key={idx} className="flex justify-between p-2 bg-red-50 rounded">
                        <span className="text-sm font-medium">{d.district_id}</span>
                        <span className="text-sm font-bold text-red-600">
                          {d.allocated_per_awc} kg/AWC
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // PAGE 2: District Progress
  const DistrictProgressTab = () => {
    if (!districtProgress) return <div>No data available</div>;

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>District-Wise Supply Chain Status</CardTitle>
            <CardDescription>Allocation, Permit, Issue comparison across all districts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">AWCs</th>
                    <th className="px-4 py-3 text-center font-semibold">Allocated (kg)</th>
                    <th className="px-4 py-3 text-center font-semibold">Permit %</th>
                    <th className="px-4 py-3 text-center font-semibold">Issued (kg)</th>
                    <th className="px-4 py-3 text-center font-semibold">Issue %</th>
                    <th className="px-4 py-3 text-center font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {districtProgress.districts.map((district, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDistrict({ district: district.district_id });
                        setIsModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 font-medium">{district.district_id}</td>
                      <td className="px-4 py-3 text-center">{district.awc_count}</td>
                      <td className="px-4 py-3 text-center">
                        {district.allocated_qty.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={district.permit_pct >= 99 ? 'text-green-600' : 'text-orange-600'}>
                          {district.permit_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {district.issued_qty.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center font-bold">
                        <span className={
                          district.issue_pct >= 5 ? 'text-green-600' :
                          district.issue_pct >= 1 ? 'text-orange-600' : 'text-red-600'
                        }>
                          {district.issue_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          district.issuance_status === 'Advanced' ? 'bg-green-100 text-green-800' :
                          district.issuance_status === 'Started' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {district.issuance_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // PAGE 3: Supply Chain Analysis
  const SupplyChainTab = () => {
    if (!issuanceAnalysis || !scatterData) return <div>No data available</div>;

    return (
      <div className="space-y-6">
        {/* Top Issuers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-green-500 border-2">
            <CardHeader>
              <CardTitle className="text-green-600">Top 10 Early Issuers</CardTitle>
              <CardDescription>Districts leading the distribution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {issuanceAnalysis.top_issuers.map((district, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                    onClick={() => {
                      setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                      setIsModalOpen(true);
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-green-900">{district.district_id}</p>
                        <p className="text-xs text-green-700">
                          {district.issued_qty.toLocaleString()} kg issued
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-600">
                          {district.share_of_state_issuance}%
                        </p>
                        <p className="text-xs text-green-700">of state</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-500 border-2">
            <CardHeader>
              <CardTitle className="text-red-600">Zero Issuance Districts</CardTitle>
              <CardDescription>{issuanceAnalysis.zero_issuance_districts.length} districts not started</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {issuanceAnalysis.zero_issuance_districts.slice(0, 20).map((district, idx) => (
                  <div 
                    key={idx} 
                    className="p-2 bg-red-50 rounded text-center cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => {
                      setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                      setIsModalOpen(true);
                    }}
                  >
                    <p className="text-sm font-semibold text-red-900">{district.district_id}</p>
                    <p className="text-xs text-red-700">
                      {(district.allocated_qty / 1000).toFixed(1)}T allocated
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scatter Plot */}
        <Card>
          <CardHeader>
            <CardTitle>Allocation vs Issuance Analysis</CardTitle>
            <CardDescription>District positioning: Allocated per AWC vs Issue %</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart margin={{ top: 20, right: 30, bottom: 60, left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="allocated_per_awc" 
                  name="Allocated per AWC (kg)" 
                  domain={[0, 100]}
                  label={{ value: 'Allocated per AWC (kg)', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="issue_pct" 
                  name="Issue %" 
                  domain={[0, 10]}
                  label={{ value: 'Issue %', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: 12 }}
                  width={50}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Scatter 
                  name="Districts" 
                  data={scatterData.scatter_data} 
                  fill="#3b82f6"
                  shape={(props) => {
                    const { cx, cy, payload } = props;
                    const [isHovered, setIsHovered] = React.useState(false);
                    
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={isHovered ? 8 : 6}
                        fill={isHovered ? '#2563eb' : '#3b82f6'}
                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                        onClick={() => {
                          // Backend returns "district" field in scatter_data
                          if (payload?.district || payload?.district_id) {
                            const districtId = payload.district || payload.district_id;
                            setSelectedDistrict({ district: districtId, district_id: districtId });
                            setIsModalOpen(true);
                          }
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                      />
                    );
                  }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Stock at Risk */}
        <Card>
          <CardHeader>
            <CardTitle>Stock at Risk (Top 10)</CardTitle>
            <CardDescription>Allocated but not yet issued - requires distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart 
                data={districtProgress.districts.slice(0, 10)}
                margin={{ top: 20, right: 30, left: 50, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="district_id" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}
                  tick={{ fontSize: 10 }}
                  interval={0}
                />
                <YAxis 
                  label={{ value: 'Stock (kg)', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: 12 }}
                  width={50}
                />
                <Tooltip 
                  contentStyle={{ fontSize: '12px' }}
                />
                <Bar 
                  dataKey="stock_at_risk" 
                  fill="#f59e0b" 
                  radius={[8, 8, 0, 0]}
                  shape={(props) => {
                    const { payload, x, y, width, height } = props;
                    const [isHovered, setIsHovered] = React.useState(false);
                    
                    const handleClick = (e) => {
                      e.stopPropagation();
                      if (payload?.district_id) {
                        setSelectedDistrict({ district: payload.district_id, district_id: payload.district_id });
                        setIsModalOpen(true);
                      }
                    };

                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={isHovered ? '#d97706' : '#f59e0b'}
                        style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                        onClick={handleClick}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                      />
                    );
                  }}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    );
  };

  // PAGE 4: Red Flags & Action
  const RedFlagsTab = () => {
    if (!redFlags) return <div>No data available</div>;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-red-500 border-2">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-red-600">
                {redFlags.zero_issuance_districts.length}
              </p>
              <p className="text-sm text-gray-600 mt-1">Zero Issuance Districts</p>
              <p className="text-xs text-red-600 mt-2">Critical priority</p>
            </CardContent>
          </Card>

          <Card className="border-orange-500 border-2">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-orange-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-orange-600">
                {redFlags.high_alloc_low_issue.length}
              </p>
              <p className="text-sm text-gray-600 mt-1">High Alloc, Low Issue</p>
              <p className="text-xs text-orange-600 mt-2">Needs acceleration</p>
            </CardContent>
          </Card>

          <Card className="border-yellow-500 border-2">
            <CardContent className="pt-6 text-center">
              <Package className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-yellow-600">
                {(stateOverview.allocated_qty - stateOverview.issued_qty) / 1000 | 0}T
              </p>
              <p className="text-sm text-gray-600 mt-1">Stock Pending</p>
              <p className="text-xs text-yellow-600 mt-2">Needs distribution</p>
            </CardContent>
          </Card>
        </div>

        {/* Zero Issuance Table */}
        <Card className="border-red-500 border-2">
          <CardHeader>
            <CardTitle className="text-red-600">Zero Issuance Districts - Immediate Action Required</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">AWCs</th>
                    <th className="px-4 py-3 text-center font-semibold">Allocated (kg)</th>
                    <th className="px-4 py-3 text-center font-semibold">Alloc/AWC</th>
                    <th className="px-4 py-3 text-left font-semibold">Priority</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {redFlags.zero_issuance_districts.map((district, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-red-50 cursor-pointer"
                      onClick={() => {
                        setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                        setIsModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 font-medium">{district.district_id}</td>
                      <td className="px-4 py-3 text-center">{district.awc_count}</td>
                      <td className="px-4 py-3 text-center">
                        {district.allocated_qty.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center font-bold">
                        {district.allocated_per_awc}
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold">
                          HIGH
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Allocation Outliers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-purple-500 border-2">
            <CardHeader>
              <CardTitle className="text-purple-600">High Allocation Outliers</CardTitle>
              <CardDescription>&gt;70 kg/AWC - Requires validation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {allocationVariance?.high_outliers.map((d, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-purple-50 rounded-lg flex justify-between cursor-pointer hover:bg-purple-100 transition-colors"
                    onClick={() => {
                      setSelectedDistrict({ district: d.district_id, district_id: d.district_id });
                      setIsModalOpen(true);
                    }}
                  >
                    <span className="font-semibold text-purple-900">{d.district_id}</span>
                    <span className="font-bold text-purple-600">{d.allocated_per_awc} kg/AWC</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-500 border-2">
            <CardHeader>
              <CardTitle className="text-blue-600">Low Allocation Outliers</CardTitle>
              <CardDescription>&lt;20 kg/AWC - Verify norms</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {allocationVariance?.low_outliers.map((d, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-blue-50 rounded-lg flex justify-between cursor-pointer hover:bg-blue-100 transition-colors"
                    onClick={() => {
                      setSelectedDistrict({ district: d.district_id, district_id: d.district_id });
                      setIsModalOpen(true);
                    }}
                  >
                    <span className="font-semibold text-blue-900">{d.district_id}</span>
                    <span className="font-bold text-blue-600">{d.allocated_per_awc} kg/AWC</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Recommendations */}
        <Card className="border-orange-500 border-2">
          <CardHeader>
            <CardTitle className="text-orange-600">Priority Action Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg">
                <h4 className="font-semibold text-red-900 mb-2">
                  🚨 Immediate (0-2 weeks): {redFlags.zero_issuance_districts.length} Districts with Zero Issuance
                </h4>
                <p className="text-sm text-red-800">
                  Action: Conduct emergency review with District Collectors and initiate immediate distribution
                </p>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-2">
                  ⚠️ High Priority: Concentration Risk
                </h4>
                <p className="text-sm text-orange-800">
                  Action: Rajkot, Bhavnagar, Patan account for 77% of issuance - diversify distribution across more districts
                </p>
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg">
                <h4 className="font-semibold text-yellow-900 mb-2">
                  📋 Medium Priority: Allocation Variance Validation
                </h4>
                <p className="text-sm text-yellow-800">
                  Action: Review 10x difference (Botad 80 kg/AWC vs Tapi 8 kg/AWC) - verify against beneficiary norms
                </p>
              </div>

              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">
                  ✅ Ongoing: Utilization Tracking Setup
                </h4>
                <p className="text-sm text-green-800">
                  Action: Establish sale/consumption data pipeline for next monthly review
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8 pt-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Hot Cooked Meal (Fortified Rice) Dashboard
            </h1>
            <p className="text-gray-600">
              Supply chain monitoring: October 2025 - January 2026 cycle (Status: 8 Nov 2025)
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="overview">State Overview</TabsTrigger>
              <TabsTrigger value="progress">District Progress</TabsTrigger>
              <TabsTrigger value="supply-chain">Supply Chain</TabsTrigger>
              <TabsTrigger value="red-flags">Red Flags</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <OverviewTab />
            </TabsContent>

            <TabsContent value="progress">
              <DistrictProgressTab />
            </TabsContent>

            <TabsContent value="supply-chain">
              <SupplyChainTab />
            </TabsContent>

            <TabsContent value="red-flags">
              <RedFlagsTab />
            </TabsContent>
          </Tabs>

        </div>
      </div>

      {/* District Detail Modal */}
      <THRDistrictDetailModal
        district={selectedDistrict}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDistrict(null);
        }}
        reportingPeriod={stateOverview?.reporting_period || "Oct-2025-Jan-2026"}
        dashboardType="hcm"
      />
    </div>
  );
};

export default HCMEnhancedDashboard;
